/**
 * Real-time listener on `tasks/{taskId}/followers` — the supporter mirror
 * native's Task Edit overlay reads (`useTaskFollowers`).
 *
 * Owner and assignee collection reads must constrain the query the same way
 * the phone does, or Firestore refuses the list. A contributor or follower
 * may read the collection unconstrained.
 */
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFirebaseDb } from '../config/firebase';
import { useLiveQuery } from './useLiveQuery';
import { toMillis } from './workDocs';

export interface TaskFollower {
  id: string;
  userId: string;
  publicDisplayName: string | null;
  photoURL: string | null;
  followedAt: number;
}

export function useTaskFollowers(
  taskId: string | null | undefined,
  options: {
    readerUid?: string | null;
    taskOwnerUid?: string | null;
    taskAssigneeUid?: string | null;
  } = {},
): { followers: TaskFollower[]; loading: boolean } {
  const { readerUid, taskOwnerUid, taskAssigneeUid } = options;
  const constraint =
    readerUid && taskOwnerUid && readerUid === taskOwnerUid
      ? 'owner'
      : readerUid && taskAssigneeUid && readerUid === taskAssigneeUid
        ? 'assignee'
        : 'open';
  const key = taskId && readerUid ? `taskFollowers:${taskId}:${readerUid}:${constraint}` : null;

  const { value, error } = useLiveQuery<TaskFollower[]>(key, (onValue, onError) => {
    const coll = collection(getFirebaseDb(), 'tasks', taskId!, 'followers');
    const scoped =
      constraint === 'owner'
        ? query(coll, where('ownerUserId', '==', taskOwnerUid))
        : constraint === 'assignee'
          ? query(coll, where('assignedUserId', '==', taskAssigneeUid))
          : coll;

    return onSnapshot(
      scoped,
      (snap) => {
        const rows = snap.docs.map((docSnap) => {
          const data = docSnap.data() as Record<string, unknown>;
          return {
            id: docSnap.id,
            userId: docSnap.id,
            publicDisplayName:
              typeof data.publicDisplayName === 'string' && data.publicDisplayName
                ? data.publicDisplayName
                : null,
            photoURL: typeof data.photoURL === 'string' && data.photoURL ? data.photoURL : null,
            followedAt: toMillis(data.followedAt),
          };
        });
        const filtered = taskAssigneeUid
          ? rows.filter((row) => row.userId !== taskAssigneeUid)
          : rows;
        filtered.sort((a, b) => a.followedAt - b.followedAt);
        onValue(filtered);
      },
      (err) => onError(err instanceof Error ? err : new Error(String(err))),
    );
  });

  return {
    followers: value ?? [],
    loading: Boolean(key) && value == null && error == null,
  };
}
